import { db } from '../config/firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { fetchPlayers, savePlayer } from './ifrData';
import { fetchMembers, C } from './programData';

// ══════════════════════════════════════════════════════════════════════
//  UTAFUTAJI MMOJA WA WACHEZAJI — IFR + Wanachama wa Chess Club
//  Wachezaji wa mashindano wanaweza kutoka pande zote mbili. Badala ya
//  kusajili mara mbili, tunachanganya orodha zote mbili kwenye
//  utafutaji mmoja. Mtu akichaguliwa kutoka Members kwa mara ya kwanza
//  kwenye tukio la IFR, rekodi yake ya IFR inatengenezwa papo hapo
//  (ikiwa imeunganishwa na Members yake) — si usajili wa pili, ni daraja.
// ══════════════════════════════════════════════════════════════════════

const RC_PLAYERS = 'ratedPlayers';

export async function searchAllPlayers() {
  const [ifrPlayers, members] = await Promise.all([
    fetchPlayers(),
    fetchMembers(), // wote, programu zote — tunachuja chess-eligible chini
  ]);

  const fromIfr = ifrPlayers
    .filter(p => p.active !== false)
    .map(p => ({
      id: p.id, source: 'ifr', fullName: p.fullName, dob: p.dob || '',
      gender: p.gender || '', school: p.school || '', clubId: p.clubId || '',
      clubName: p.clubName || '', establishedClassical: !!p.established,
      ifr: p.established ? p.ifr : null, sourceMemberId: p.sourceMemberId || null,
    }));

  const fromMembers = members
    .filter(m => m.type === 'mtoto' || m.type === 'mtu_mzima' || (m.type === 'mzazi' && m.learnsChess))
    .map(m => ({
      id: m.id, source: 'member', fullName: m.fullName, dob: m.dob || '',
      gender: m.gender || '', school: m.school || m.centre || m.area || '',
      clubId: m.clubId || '', clubName: m.clubName || '',
      establishedClassical: false, ifr: null, memberType: m.type,
    }));

  // Wale washawahi kuunganishwa (ifr player ana sourceMemberId) —
  // waondoe upande wa Members ili wasionekane mara mbili
  const linkedMemberIds = new Set(fromIfr.map(p => p.sourceMemberId).filter(Boolean));
  const filteredMembers = fromMembers.filter(m => !linkedMemberIds.has(m.id));

  return [...fromIfr, ...filteredMembers]
    .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
}

export function filterPlayers(list, query) {
  if (!query?.trim()) return list;
  const q = query.trim().toLowerCase();
  return list.filter(p =>
    (p.fullName || '').toLowerCase().includes(q)
    || (p.school || '').toLowerCase().includes(q)
    || (p.clubName || '').toLowerCase().includes(q)
  );
}

// ══════════════════════════════════════════════════════════════════════
//  MPANGILIO WA A-Z NA CLUB — kutumika KILA PANAPOCHAGULIWA MTU popote
//  kwenye App (orodha za mashindano, dropdown, utafutaji). Vilabu
//  vyenyewe vinapangwa A-Z, na ndani ya kila club wanachama wake A-Z.
//  Wasio na club wanakusanywa chini kabisa kwenye kundi "Hawana Club".
// ══════════════════════════════════════════════════════════════════════
const NO_CLUB_LABEL = 'Hawana Club';

export function groupByClub(list) {
  const groups = {};
  (list || []).forEach(p => {
    const key = p.clubName && p.clubName.trim() ? p.clubName.trim() : NO_CLUB_LABEL;
    (groups[key] = groups[key] || []).push(p);
  });

  const clubNames = Object.keys(groups)
    .filter(k => k !== NO_CLUB_LABEL)
    .sort((a, b) => a.localeCompare(b));

  const out = clubNames.map(name => ({
    clubName: name,
    members: [...groups[name]].sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '')),
  }));

  if (groups[NO_CLUB_LABEL]?.length) {
    out.push({
      clubName: NO_CLUB_LABEL,
      members: [...groups[NO_CLUB_LABEL]].sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '')),
    });
  }
  return out;
}

// ══════════════════════════════════════════════════════════════════════
//  "BANDIKA ORODHA" — mtumiaji anabandika majina mengi kwa wakati mmoja
//  (mf. kutoka Excel au daftari), mstari mmoja kwa kila jina. Tunalinganisha
//  kila jina na orodha ya wachezaji waliopo; yasiyopatikana yanabaki
//  "mapya" na yataundiwa rekodi mpya ya IFR kwa jina tu wakati wa kuongeza.
// ══════════════════════════════════════════════════════════════════════
export function resolvePastedNames(text, allPlayers) {
  const lines = (text || '')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);

  const seen = new Set();
  const uniqueLines = [];
  lines.forEach(l => {
    const key = l.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    uniqueLines.push(l);
  });

  const matched = [];
  const unmatched = [];
  uniqueLines.forEach(name => {
    const found = (allPlayers || []).find(p => (p.fullName || '').toLowerCase() === name.toLowerCase());
    if (found) matched.push(found);
    else unmatched.push(name);
  });

  return { matched, unmatched };
}

// Mtu wa Members anapochaguliwa kwa mara ya kwanza kwenye tukio la IFR,
// tunamtengenezea rekodi ya ratedPlayers iliyounganishwa na Members yake
// — si usajili wa pili, ni daraja tu. Ikiwa tayari ana rekodi (kwa sababu
// alichaguliwa awali), tunairudisha hiyo hiyo bila kuongeza nakala.
export async function ensureIfrPlayer(picked) {
  if (picked.source === 'ifr') return picked.id;

  // Jina kabisa jipya (halikutoka Members wala IFR) — lililobandikwa kwenye
  // orodha ya mashindano. Tunamtengenezea rekodi ya IFR kwa jina tu.
  if (picked.source === 'new') {
    const existingByName = (await fetchPlayers())
      .find(p => (p.fullName || '').toLowerCase() === (picked.fullName || '').toLowerCase());
    if (existingByName) return existingByName.id;
    return savePlayer({ fullName: picked.fullName, dob: '', gender: '', school: '', clubId: '', clubName: '' });
  }

  const existing = (await fetchPlayers()).find(p => p.sourceMemberId === picked.id);
  if (existing) return existing.id;

  return savePlayer({
    fullName: picked.fullName, dob: picked.dob || '', gender: picked.gender || '',
    school: picked.school || '', clubId: picked.clubId || '', clubName: picked.clubName || '',
    sourceMemberId: picked.id,
  });
}

// Kurekebisha taarifa "hapo hapo" bila kutoka kwenye ukurasa wa
// mashindano — inaandika kwenye chanzo halisi (ratedPlayers au members)
export async function quickFixPlayer(picked, patch) {
  const targetCollection = picked.source === 'ifr' ? RC_PLAYERS : C.members;
  await updateDoc(doc(db, targetCollection, picked.id), { ...patch, updatedAt: serverTimestamp() });
}
